import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const RevenueChart = () => {
  const [timePeriod, setTimePeriod] = useState('6months');

  const revenueData = [
    { month: 'Jan', revenue: 45000, projection: 48000, target: 50000 },
    { month: 'Feb', revenue: 52000, projection: 54000, target: 55000 },
    { month: 'Mar', revenue: 48000, projection: 51000, target: 52000 },
    { month: 'Apr', revenue: 61000, projection: 63000, target: 60000 },
    { month: 'May', revenue: 55000, projection: 58000, target: 58000 },
    { month: 'Jun', revenue: 67000, projection: 70000, target: 65000 },
    { month: 'Jul', revenue: 72000, projection: 75000, target: 70000 },
    { month: 'Aug', revenue: 68000, projection: 71000, target: 72000 },
    { month: 'Sep', revenue: 78000, projection: 80000, target: 75000 },
    { month: 'Oct', revenue: 82000, projection: 85000, target: 80000 },
    { month: 'Nov', revenue: 88000, projection: 90000, target: 85000 },
    { month: 'Dec', revenue: 95000, projection: 98000, target: 90000 }
  ];

  const getFilteredData = () => {
    if (timePeriod === '3months') return revenueData?.slice(-3);
    if (timePeriod === '6months') return revenueData?.slice(-6);
    return revenueData;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-md p-3 elevation-lg">
          <p className="text-sm font-medium text-foreground font-body mb-2">
            {payload?.[0]?.payload?.month}
          </p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground font-caption">
                {entry?.name}:
              </span>
              <span className="font-medium text-foreground font-body">
                ₹{entry?.value?.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground font-heading">
            Revenue Trend Analysis
          </h3>
          <p className="text-sm text-muted-foreground font-caption mt-1">
            Track your revenue performance over time
          </p>
        </div>
        <div className="flex items-center gap-2">
          {['3months', '6months', 'year']?.map((period) => (
            <button
              key={period}
              onClick={() => setTimePeriod(period)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium font-caption transition-smooth ${
                timePeriod === period
                  ? 'bg-primary text-primary-foreground elevation-sm'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {period === '3months' ? '3M' : period === '6months' ? '6M' : '1Y'}
            </button>
          ))}
        </div>
      </div>
      <div className="w-full h-64 md:h-80" aria-label="Revenue trend line chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={getFilteredData()} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="month" 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px', fontFamily: 'Source Sans 3' }}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px', fontFamily: 'Source Sans 3' }}
              tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '12px', fontFamily: 'Source Sans 3' }}
              iconType="line"
            />
            <Line 
              type="monotone" 
              dataKey="revenue" 
              stroke="var(--color-primary)" 
              strokeWidth={2.5}
              dot={{ fill: 'var(--color-primary)', r: 4 }}
              activeDot={{ r: 6 }}
              name="Actual Revenue"
            />
            <Line 
              type="monotone" 
              dataKey="projection" 
              stroke="var(--color-secondary)" 
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: 'var(--color-secondary)', r: 3 }}
              name="Projection"
            />
            <Line 
              type="monotone" 
              dataKey="target" 
              stroke="var(--color-accent)" 
              strokeWidth={2}
              strokeDasharray="3 3"
              dot={{ fill: 'var(--color-accent)', r: 3 }}
              name="Target"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center">
            <Icon name="DollarSign" size={16} color="var(--color-primary)" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground font-caption">Current Month</p>
            <p className="text-sm font-semibold text-foreground font-body">$95,000</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-success/10 flex items-center justify-center">
            <Icon name="TrendingUp" size={16} color="var(--color-success)" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground font-caption">Growth Rate</p>
            <p className="text-sm font-semibold text-success font-body">+8.0%</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-accent/10 flex items-center justify-center">
            <Icon name="Target" size={16} color="var(--color-accent)" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground font-caption">Target Achievement</p>
            <p className="text-sm font-semibold text-accent font-body">105.6%</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueChart;